import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Space, message, Tag, Modal, Descriptions, Divider, Form, Input, Select, DatePicker, Row, Col, Alert } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined, PrinterOutlined, SearchOutlined, ClearOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../services/api';
import { useCanDelete } from '../hooks/useCanDelete';

interface Document {
  id: number;
  documentNo: string;
  documentDate: string;
  documentTypeName: string;
  warehouseName?: string;
  accountName?: string;
  vehiclePlate?: string | null;
  status: string;
  totalAmount: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  currencyId?: number;
  currencyCode?: string;
  currencySymbol?: string;
  exchangeRate?: number;
  reversalOfDocumentId?: number | null;
  cancelledByDocumentId?: number | null;
}

interface DocumentItem {
  lineNo: number;
  productCode: string;
  productName: string;
  variantSku?: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  discountRate: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  unitPriceEx?: number;
}

interface DocumentDetail extends Document {
  totalTax: number;
  items: DocumentItem[];
}

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isDefault?: boolean;
}

interface DocumentTypeOption {
  id: number;
  typeCode: string;
  typeName: string;
}

const Documents: React.FC = () => {
  const canDelete = useCanDelete();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currencyMap, setCurrencyMap] = useState<Record<number, Currency>>({});
  const [baseCurrencyCode, setBaseCurrencyCode] = useState<string>('TRY');
  const [baseCurrencySymbol, setBaseCurrencySymbol] = useState<string>('₺');
  const printRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [filterDocumentTypeId, setFilterDocumentTypeId] = useState<number | undefined>();
  const [filterAccountName, setFilterAccountName] = useState<string>('');
  const [filterDateRange, setFilterDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Document | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [offerCancelVisible, setOfferCancelVisible] = useState(false);
  const [offerCancelDoc, setOfferCancelDoc] = useState<Document | null>(null);
  const [offerCancelMessage, setOfferCancelMessage] = useState('');
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeOption[]>([]);
  const [filterForm] = Form.useForm();

  useEffect(() => {
    loadDocuments();
    loadCurrencies();
    loadDocumentTypes();
  }, []);

  const loadDocumentTypes = async () => {
    try {
      const { data } = await api.get<DocumentTypeOption[]>('/documenttypes', { params: { stockOnly: true } });
      setDocumentTypes(data ?? []);
    } catch (error) {
      console.error('Document types load error:', error);
    }
  };

  const loadDocuments = async (overrides?: {
    documentTypeId?: number;
    accountName?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      setLoading(true);
      const docTypeId = overrides?.documentTypeId !== undefined ? overrides.documentTypeId : filterDocumentTypeId;
      const accName = overrides?.accountName !== undefined ? overrides.accountName : filterAccountName?.trim();
      const from = overrides?.dateFrom ?? filterDateRange?.[0]?.format('YYYY-MM-DD');
      const to = overrides?.dateTo ?? filterDateRange?.[1]?.format('YYYY-MM-DD');
      const params: Record<string, string | number | undefined> = {};
      if (docTypeId != null) params.documentTypeId = docTypeId;
      if (accName) params.accountName = accName;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
      const { data } = await api.get('/documents', { params });
      setDocuments(data);
      if (overrides) {
        setFilterDocumentTypeId(overrides.documentTypeId);
        setFilterAccountName(overrides.accountName ?? '');
        setFilterDateRange(
          overrides.dateFrom && overrides.dateTo
            ? [dayjs(overrides.dateFrom), dayjs(overrides.dateTo)]
            : null
        );
      }
    } catch (error) {
      message.error('Fişler yüklenirken hata oluştu');
      console.error('Documents load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const v = filterForm.getFieldsValue();
    loadDocuments({
      documentTypeId: v.documentTypeId,
      accountName: v.accountName?.trim(),
      dateFrom: v.dateRange?.[0]?.format('YYYY-MM-DD'),
      dateTo: v.dateRange?.[1]?.format('YYYY-MM-DD'),
    });
  };

  const handleClearFilter = () => {
    filterForm.resetFields();
    setFilterDocumentTypeId(undefined);
    setFilterAccountName('');
    setFilterDateRange(null);
    loadDocuments({});
  };

const loadCurrencies = async () => {
  try {
    const { data } = await api.get<{ baseCurrencyCode: string; items: Currency[] }>('/currencies');
    if (data?.items) {
      const map: Record<number, Currency> = {};
      data.items.forEach(currency => {
        map[currency.id] = currency;
      });
      setCurrencyMap(map);
      const baseCode = data.baseCurrencyCode || 'TRY';
      setBaseCurrencyCode(baseCode);
      const baseCurrency = data.items.find(c => c.code === baseCode) || data.items.find(c => c.isDefault);
      setBaseCurrencySymbol(baseCurrency?.symbol || '₺');
    }
  } catch (error) {
    console.error('Para birimleri yüklenirken hata oluştu:', error);
  }
};

const formatMoney = (amount: number = 0, currencyCode?: string, currencySymbol?: string) => {
  const code = currencyCode || 'TRY';
  const symbol = currencySymbol || '₺';
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: code
    }).format(amount || 0);
  } catch {
    const value = Number.isFinite(amount) ? amount : 0;
    return `${symbol} ${value.toFixed(2)}`;
  }
};

const formatBaseMoney = (amount: number = 0) => formatMoney(amount, baseCurrencyCode, baseCurrencySymbol);

const formatDocumentCurrency = (amount: number = 0, doc?: Document | DocumentDetail | null) =>
  formatMoney(amount, doc?.currencyCode || baseCurrencyCode, doc?.currencySymbol || baseCurrencySymbol);

  const canCancelDocument = (record: Document) =>
    canDelete &&
    record.status !== 'Cancelled' &&
    !record.cancelledByDocumentId &&
    !record.reversalOfDocumentId;

  const handleDelete = async (id: number) => {
    const target = deleteTarget ?? documents.find((d) => d.id === id) ?? null;
    try {
      setDeleteSubmitting(true);
      setLoading(true);
      await api.delete(`/documents/${id}`);
      message.success('Fiş silindi (stok ve cari geri alındı)');
      setDeleteConfirmVisible(false);
      setDeleteTarget(null);
      loadDocuments();
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.code === 'SubsequentMovements' && target && canCancelDocument(target)) {
        setDeleteConfirmVisible(false);
        setDeleteTarget(null);
        setOfferCancelDoc(target);
        setOfferCancelMessage(
          data?.message ||
            'Bu fişten sonra stok hareketi oluşmuş. Güvenli silinemiyor. İptal etmek ister misiniz?'
        );
        setOfferCancelVisible(true);
      } else {
        message.error(data?.message || 'Fiş silinirken hata oluştu');
      }
      console.error('Delete error:', error);
    } finally {
      setDeleteSubmitting(false);
      setLoading(false);
    }
  };

  const openDeleteConfirm = (record: Document) => {
    setDeleteTarget(record);
    setDeleteConfirmVisible(true);
  };

  const openCancelConfirm = (record: Document) => {
    setCancelTarget(record);
    setCancelConfirmVisible(true);
  };

  const executeCancel = async (doc: Document, allowNegative = false) => {
    try {
      setCancelSubmitting(true);
      setLoading(true);
      const { data } = await api.post(`/documents/${doc.id}/cancel`, {
        allowNegativeStock: allowNegative,
      });
      message.success(
        `Fiş iptal edildi. Ters fiş: ${data?.reversalDocumentNo ?? ''}`
      );
      setCancelConfirmVisible(false);
      setCancelTarget(null);
      setOfferCancelVisible(false);
      setOfferCancelDoc(null);
      loadDocuments();
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.code === 'InsufficientStock' && data?.items?.length) {
        const itemList = data.items
          .map(
            (i: any) =>
              `• ${i.productName ?? 'Ürün'}: mevcut ${i.currentStock ?? 0}, istenen ${i.requestedQuantity ?? 0}`
          )
          .join('\n');
        Modal.confirm({
          title: 'Yetersiz stok',
          content: (
            <div>
              <p>{data.message ?? 'Stok yetersiz. Negatife düşürerek iptal edilsin mi?'}</p>
              <pre style={{ marginTop: 8, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{itemList}</pre>
            </div>
          ),
          okText: 'Evet, iptal et',
          cancelText: 'Vazgeç',
          onOk: () => executeCancel(doc, true),
        });
      } else {
        message.error(data?.message || 'Fiş iptal edilirken hata oluştu');
      }
    } finally {
      setCancelSubmitting(false);
      setLoading(false);
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      setDetailModalVisible(true);
      const { data } = await api.get(`/documents/${id}`);
      setSelectedDocument(data);
    } catch (error) {
      message.error('Fiş detayı yüklenirken hata oluştu');
      console.error('Detail load error:', error);
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Approved': return 'green';
      case 'Cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Draft': return 'Taslak';
      case 'Approved': return 'Onaylandı';
      case 'Cancelled': return 'İptal';
      default: return status;
    }
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Fiş No',
      dataIndex: 'documentNo',
      key: 'documentNo',
      width: 120,
    },
    {
      title: 'Tarih',
      dataIndex: 'documentDate',
      key: 'documentDate',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'Fiş Tipi',
      dataIndex: 'documentTypeName',
      key: 'documentTypeName',
      width: 150,
    },
    {
      title: 'Depo',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Cari',
      dataIndex: 'accountName',
      key: 'accountName',
      render: (text: string, record: Document) => (
        <div>
          <div>{text || '-'}</div>
          {record.vehiclePlate && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.vehiclePlate}</div>
          )}
        </div>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string, record: Document) => (
        <div>
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
          {record.reversalOfDocumentId ? (
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Ters fiş</div>
          ) : null}
          {record.cancelledByDocumentId ? (
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>İptal edildi</div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Tutar',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (_: number, record: Document) => {
        const baseAmountFormatted = formatBaseMoney(record.totalAmount);
        const docCurrencyDiffers =
          !!record.currencyCode && record.currencyCode !== baseCurrencyCode && !!record.exchangeRate && record.exchangeRate > 0;
        let originalAmountFormatted: string | null = null;

        if (docCurrencyDiffers && record.exchangeRate) {
          const originalAmount = record.totalAmount / record.exchangeRate;
          originalAmountFormatted = formatDocumentCurrency(originalAmount, record);
        }

        return (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold' }}>{baseAmountFormatted}</div>
            {docCurrencyDiffers && (
              <>
                {originalAmountFormatted && (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    ≈ {originalAmountFormatted}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#9a9a9a' }}>
                  1 {record.currencyCode} = {record.exchangeRate} {baseCurrencyCode}
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: 'Açıklama',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          />
          <Button
            size="small"
            icon={<CopyOutlined />}
            title="Kopyala (yeni fiş formu)"
            onClick={() => navigate(`/documents/add?copyFrom=${record.id}`)}
          />
          {canCancelDocument(record) && (
            <Button
              size="small"
              icon={<StopOutlined />}
              onClick={() => openCancelConfirm(record)}
              title="İptal (ters fiş oluştur)"
            >
              İptal
            </Button>
          )}
          {canDelete &&
            record.status !== 'Cancelled' &&
            !record.cancelledByDocumentId &&
            !record.reversalOfDocumentId && (
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => openDeleteConfirm(record)}
              />
            )}
        </Space>
      ),
    },
  ];

  const itemColumns: ColumnsType<DocumentItem> = [
    {
      title: 'Sıra',
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 60,
    },
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Varyant',
      dataIndex: 'variantSku',
      key: 'variantSku',
      render: (text: string) => text || '-',
    },
    {
      title: 'Birim',
      dataIndex: 'unitName',
      key: 'unitName',
      width: 80,
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (value: number) => value.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (value: number, record: DocumentItem) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        return (
          <div>
            <div>{baseValue}</div>
            {showOriginal && record.unitPriceEx && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {formatDocumentCurrency(record.unitPriceEx, selectedDocument)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'İskonto',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      width: 100,
      align: 'right',
      render: (value: number) => {
        if (!value || value === 0) return '-';
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;

        return (
          <div>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'KDV',
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      width: 100,
      align: 'right',
      render: (value: number) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;
        return (
          <div>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tutar',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 120,
      align: 'right',
      render: (value: number) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;
        return (
          <div style={{ fontWeight: 'bold' }}>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const rangePickerLocale = undefined;

  return (
    <>
    <Card size="small" style={{ marginBottom: 16 }}>
      <Form form={filterForm} layout="vertical" onFinish={handleFilter}>
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="documentTypeId" label="Fiş tipi">
              <Select
                placeholder="Tümü"
                allowClear
                showSearch
                optionFilterProp="label"
                options={documentTypes.map((dt) => ({ value: dt.id, label: dt.typeName }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="accountName" label="Cari adı">
              <Input placeholder="Cari adı ile ara" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="dateRange" label="Tarih aralığı">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                placeholder={['Başlangıç', 'Bitiş']}
                locale={rangePickerLocale}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Form.Item label=" ">
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  Filtrele
                </Button>
              </Form.Item>
              <Form.Item label=" ">
                <Button icon={<ClearOutlined />} onClick={handleClearFilter}>
                  Temizle
                </Button>
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>

    <Card
      title="Fişler"
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => navigate('/documents/add')}
        >
          Yeni Fiş
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={documents}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
        scroll={{ x: 1200 }}
      />
    </Card>

      <Modal
        title="Fiş Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={1200}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            Yazdır
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>,
        ]}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>Yükleniyor...</div>
        ) : selectedDocument ? (
          <div ref={printRef}>
            <style>{`
              @media print {
                @page {
                  size: A4 landscape;
                  margin: 15mm;
                }
                body * {
                  visibility: hidden;
                }
                #print-content, #print-content * {
                  visibility: visible;
                }
                #print-content {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                }
                .no-print {
                  display: none !important;
                }
                .ant-table-wrapper {
                  overflow: visible !important;
                }
                .ant-table {
                  overflow: visible !important;
                  font-size: 11px !important;
                }
                .ant-table-container {
                  overflow: visible !important;
                }
                .ant-table-body {
                  overflow: visible !important;
                }
                .ant-table-content {
                  overflow: visible !important;
                }
                .print-table table {
                  width: 100% !important;
                  table-layout: auto !important;
                }
                .print-table td, .print-table th {
                  white-space: nowrap !important;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  padding: 4px 6px !important;
                  font-size: 10px !important;
                }
                .print-table td:nth-child(1), .print-table th:nth-child(1) { width: 5% !important; }
                .print-table td:nth-child(2), .print-table th:nth-child(2) { width: 10% !important; }
                .print-table td:nth-child(3), .print-table th:nth-child(3) { width: 20% !important; white-space: normal !important; }
                .print-table td:nth-child(4), .print-table th:nth-child(4) { width: 10% !important; }
                .print-table td:nth-child(5), .print-table th:nth-child(5) { width: 8% !important; }
                .print-table td:nth-child(6), .print-table th:nth-child(6) { width: 10% !important; }
                .print-table td:nth-child(7), .print-table th:nth-child(7) { width: 12% !important; }
                .print-table td:nth-child(8), .print-table th:nth-child(8) { width: 10% !important; }
                .print-table td:nth-child(9), .print-table th:nth-child(9) { width: 10% !important; }
                .print-table td:nth-child(10), .print-table th:nth-child(10) { width: 12% !important; }
                .ant-descriptions {
                  font-size: 11px !important;
                }
                .ant-descriptions-item-label,
                .ant-descriptions-item-content {
                  padding: 8px !important;
                  font-size: 11px !important;
                }
              }
            `}</style>
            <div id="print-content">
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0 }}>{selectedDocument.documentTypeName}</h2>
                <p style={{ margin: '5px 0', color: '#666' }}>Fiş No: {selectedDocument.documentNo}</p>
              </div>

              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Fiş No">{selectedDocument.documentNo}</Descriptions.Item>
                <Descriptions.Item label="Tarih">
                  {new Date(selectedDocument.documentDate).toLocaleDateString('tr-TR')}
                </Descriptions.Item>
                <Descriptions.Item label="Fiş Tipi">{selectedDocument.documentTypeName}</Descriptions.Item>
                <Descriptions.Item label="Durum">
                  <Tag color={getStatusColor(selectedDocument.status)}>
                    {getStatusText(selectedDocument.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Depo">{selectedDocument.warehouseName || '-'}</Descriptions.Item>
                <Descriptions.Item label="Cari">
                  <div>{selectedDocument.accountName || '-'}</div>
                  {selectedDocument.vehiclePlate && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{selectedDocument.vehiclePlate}</div>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Para Birimi">
                  {selectedDocument.currencySymbol || baseCurrencySymbol} {selectedDocument.currencyCode || baseCurrencyCode}
                  {selectedDocument.currencyCode && selectedDocument.currencyCode !== baseCurrencyCode && selectedDocument.exchangeRate && (
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      1 {selectedDocument.currencyCode} = {selectedDocument.exchangeRate} {baseCurrencyCode}
                    </div>
                  )}
                </Descriptions.Item>
              <Descriptions.Item label="Genel Toplam (KDV Hariç)">
                {(() => {
                  const baseSubTotal = (selectedDocument.totalAmount || 0) - (selectedDocument.totalTax || 0);
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalSubTotal = showOriginal
                    ? baseSubTotal / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseSubTotal)}</strong>
                      {showOriginal && originalSubTotal !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalSubTotal, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Sivisiz Toplam">
                {(() => {
                  const baseAmount = (selectedDocument.totalAmount || 0) - (selectedDocument.totalTax || 0);
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalAmount = showOriginal ? baseAmount / (selectedDocument.exchangeRate || 1) : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseAmount)}</strong>
                      {showOriginal && originalAmount !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalAmount, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="KDV Toplamı">
                {(() => {
                  const baseTax = selectedDocument.totalTax || 0;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalTax = showOriginal
                    ? baseTax / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseTax)}</strong>
                      {showOriginal && originalTax !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalTax, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Genel Toplam (KDV Dahil)">
                {(() => {
                  const baseTotal = selectedDocument.totalAmount || 0;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalTotal = showOriginal
                    ? baseTotal / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseTotal)}</strong>
                      {showOriginal && originalTotal !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalTotal, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
                {selectedDocument.notes && (
                  <Descriptions.Item label="Açıklama" span={2}>{selectedDocument.notes}</Descriptions.Item>
                )}
              </Descriptions>

              <Divider>Fiş Kalemleri</Divider>

              <Table
                columns={itemColumns}
                dataSource={selectedDocument.items}
                rowKey="lineNo"
                pagination={false}
                size="small"
                className="print-table"
                summary={(pageData) => {
                  const totalAmount = selectedDocument.totalAmount;
                  const totalTax = selectedDocument.totalTax;
                  const subTotal = totalAmount - totalTax;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalSubTotal = showOriginal ? subTotal / (selectedDocument.exchangeRate || 1) : null;
                  const originalTax = showOriginal ? totalTax / (selectedDocument.exchangeRate || 1) : null;
                  const originalTotal = showOriginal ? totalAmount / (selectedDocument.exchangeRate || 1) : null;

                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong>Ara Toplam:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong>{formatBaseMoney(subTotal)}</strong>
                            {showOriginal && originalSubTotal !== null && (
                              <div style={{ fontSize: 11, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalSubTotal, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong>KDV:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong>{formatBaseMoney(totalTax)}</strong>
                            {showOriginal && originalTax !== null && (
                              <div style={{ fontSize: 11, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalTax, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong style={{ fontSize: 16 }}>GENEL TOPLAM:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong style={{ fontSize: 16, color: '#1890ff' }}>
                              {formatBaseMoney(totalAmount)}
                            </strong>
                            {showOriginal && originalTotal !== null && (
                              <div style={{ fontSize: 12, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalTotal, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Fiş silme onayı"
        open={deleteConfirmVisible}
        onCancel={() => {
          if (deleteSubmitting) return;
          setDeleteConfirmVisible(false);
          setDeleteTarget(null);
        }}
        onOk={() => deleteTarget && handleDelete(deleteTarget.id)}
        okText="Sil ve geri al"
        okButtonProps={{ danger: true, loading: deleteSubmitting }}
        cancelText="Vazgeç"
        confirmLoading={deleteSubmitting}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Bu işlem soft-delete yapar; stok ve cari etkileri geri alınır."
          description="Fişten sonra aynı ürün/depo için başka hareket varsa silme engellenir; isterseniz iptal (ters fiş) kullanın."
        />
        {deleteTarget && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Fiş No">{deleteTarget.documentNo}</Descriptions.Item>
            <Descriptions.Item label="Tip">{deleteTarget.documentTypeName}</Descriptions.Item>
            <Descriptions.Item label="Tarih">
              {dayjs(deleteTarget.documentDate).format('DD.MM.YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Cari">{deleteTarget.accountName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Depo">{deleteTarget.warehouseName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Toplam">
              {formatDocumentCurrency(deleteTarget.totalAmount, deleteTarget)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="Fiş iptal onayı"
        open={cancelConfirmVisible}
        onCancel={() => {
          if (cancelSubmitting) return;
          setCancelConfirmVisible(false);
          setCancelTarget(null);
        }}
        onOk={() => cancelTarget && executeCancel(cancelTarget)}
        okText="İptal fişi oluştur"
        okButtonProps={{ danger: true, loading: cancelSubmitting }}
        cancelText="Vazgeç"
        confirmLoading={cancelSubmitting}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Orijinal fiş listede kalır (İptal); ters iptal fişi oluşturulur."
          description="Stok ve cari, ters fiş üzerinden dengelenir. Denetim izi korunur."
        />
        {cancelTarget && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Fiş No">{cancelTarget.documentNo}</Descriptions.Item>
            <Descriptions.Item label="Tip">{cancelTarget.documentTypeName}</Descriptions.Item>
            <Descriptions.Item label="Tarih">
              {dayjs(cancelTarget.documentDate).format('DD.MM.YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Cari">{cancelTarget.accountName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Depo">{cancelTarget.warehouseName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Toplam">
              {formatDocumentCurrency(cancelTarget.totalAmount, cancelTarget)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="Silinemedi — iptal edilsin mi?"
        open={offerCancelVisible}
        onCancel={() => {
          setOfferCancelVisible(false);
          setOfferCancelDoc(null);
        }}
        onOk={() => offerCancelDoc && executeCancel(offerCancelDoc)}
        okText="Evet, iptal et"
        okButtonProps={{ danger: true, loading: cancelSubmitting }}
        cancelText="Vazgeç"
        confirmLoading={cancelSubmitting}
        destroyOnClose
      >
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={offerCancelMessage} />
        {offerCancelDoc && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Fiş No">{offerCancelDoc.documentNo}</Descriptions.Item>
            <Descriptions.Item label="Cari">{offerCancelDoc.accountName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Toplam">
              {formatDocumentCurrency(offerCancelDoc.totalAmount, offerCancelDoc)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default Documents;
